/**
 * Standalone API client
 * Makes direct HTTP requests to backend API
 */

import type { IApiClient } from './types'
import type { Conversation, Message } from '../types/chat'
import { getEnvironmentConfig } from './environment'

export class StandaloneApiClient implements IApiClient {
  private baseUrl: string
  private getAuthToken: () => string | null

  constructor(getAuthToken: () => string | null, baseURL?: string) {
    const config = getEnvironmentConfig()
    
    if (baseURL) {
      this.baseUrl = baseURL.endsWith('/api') 
        ? baseURL 
        : `${baseURL.replace(/\/$/, '')}/api`;
    } else {
      this.baseUrl = `https://${config.apiHost}/api`
      
      if (config.environment === 'local') {
        this.baseUrl = `http://${config.apiHost}/api`
      }
    }
    
    this.getAuthToken = getAuthToken
  }

  getBaseUrl(): string {
    return this.baseUrl
  }

  async fetchConversations(params?: {
    channel?: string
    fanpageName?: string
    search?: string
  }): Promise<Conversation[]> {
    const queryParams = new URLSearchParams()
    if (params?.channel) queryParams.set('channel', params.channel)
    if (params?.fanpageName) queryParams.set('fanpageName', params.fanpageName)
    if (params?.search) queryParams.set('search', params.search)

    return this.request<Conversation[]>(
      `/conversations?${queryParams.toString()}`
    )
  }

  async fetchMessages(
    conversationId: string,
    params?: { limit?: number; offset?: number; before?: string }
  ): Promise<Message[]> {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.set('limit', params.limit.toString())
    if (params?.offset) queryParams.set('offset', params.offset.toString())
    if (params?.before) queryParams.set('before', params.before)

    return this.request<Message[]>(
      `/conversations/${conversationId}/messages?${queryParams.toString()}`
    )
  }

  async sendMessage(
    conversationId: string,
    content: string,
    attachments?: File[]
  ): Promise<Message> {
    const formData = new FormData()
    formData.append('content', content)

    if (attachments) {
      attachments.forEach((file, index) => {
        formData.append(`attachment_${index}`, file)
      })
    }

    return this.request<Message>(
      `/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        body: formData
      }
    )
  }

  async markAsRead(conversationId: string, messageIds: string[]): Promise<void> {
    await this.request<void>(
      `/conversations/${conversationId}/read`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageIds })
      }
    )
  }

  async uploadFile(file: File): Promise<{ url: string; id: string }> {
    const formData = new FormData()
    formData.append('file', file)

    return this.request<{ url: string; id: string }>(
      '/upload',
      {
        method: 'POST',
        body: formData
      }
    )
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const token = this.getAuthToken()

    const headers = new Headers(options.headers)
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    if (!(options.body instanceof FormData) && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }

    const response = await fetch(url, {
      ...options,
      headers
    })

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`)
    }

    return response.json() as Promise<T>
  }
}
